import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';
import { useState } from 'react';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import ACAFormsBulkUploadTemplate from '@/components/ui/ACAFormsBulkUploadTemplate';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const ACAFormsBulkUpload = () => {
  const [isBulkUploadTemplate, setIsBulkUploadTemplate] = useState(false);

  return (
    <AppLayout
      meta={
        <Meta
          title="ACA Forms Bulk Upload | File 1095-B & 1095-C Online"
          description="Upload and e-file ACA Forms 1095-B & 1095-C in bulk with TaxZerone. Fast, accurate filing with Excel templates. Start at low as $0.59 per form"
          keywords=""
          canonical="resources/information-returns/aca-forms-bulk-upload/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <ACAFormsBulkUploadTemplate
        isBulkUploadTemplate={isBulkUploadTemplate}
        setIsBulkUploadTemplate={setIsBulkUploadTemplate}
      />
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          ACA Forms{' '}
          <span className="highlight-container">
            <span className="highlight"> Bulk Upload</span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          TaxZerone's Bulk Upload feature lets you file multiple ACA forms at
          once in <br />
          just a few easy steps — it's fast, accurate, and saves you time!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=aca-forms-bulk-upload`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/aca-forms-bulk-upload/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    ACA Forms Bulk Upload at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        {"TaxZerone's"}{' '}
                        <strong style={{ fontWeight: 600 }}>
                          ACA Forms Bulk Upload
                        </strong>
                        {
                          ' feature allows employers and health coverage providers to upload and e-file multiple '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          Forms 1095-B and 1095-C
                        </strong>
                        {' using a single Excel or CSV file, making ACA '}
                        reporting
                        {' faster and more efficient.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        T axZerone
                        {' supports '}
                        <strong style={{ fontWeight: 600 }}>
                          Forms 1095-B and 1095-C
                        </strong>
                        {', while '}
                        <strong style={{ fontWeight: 600 }}>
                          Forms 1094-B and 1094-C
                        </strong>
                        {
                          ' are generated automatically during the filing process, '
                        }
                        eliminating
                        {' manual preparation of transmittal forms.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {'TaxZerone accepts '}
                        <strong style={{ fontWeight: 600 }}>
                          XLSX, XLS, and CSV
                        </strong>
                        {
                          ' files through IRS-ready templates, validates your data for errors before submission, and allows unlimited corrected re-uploads at no additional cost.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        {'The bulk upload process includes '}
                        <strong style={{ fontWeight: 600 }}>
                          downloading the template
                        </strong>
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          entering employee
                        </strong>
                        {' or '}
                        <strong style={{ fontWeight: 600 }}>
                          recipient information
                        </strong>
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          uploading the completed file
                        </strong>
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          reviewing validation results
                        </strong>
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          correcting errors if needed
                        </strong>
                        {', and '}
                        <strong style={{ fontWeight: 600 }}>
                          securely e-filing
                        </strong>
                        {' directly with the IRS.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        {'You can track every submission through the '}
                        <strong style={{ fontWeight: 600 }}>
                          Information Returns Dashboard
                        </strong>
                        {', with real-time statuses such as '}
                        <strong style={{ fontWeight: 600 }}>
                          In Progress, Transmitted, Accepted,
                        </strong>
                        {' and '}
                        <strong style={{ fontWeight: 600 }}>Rejected</strong>.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Affordable Care Act (ACA), employers and health coverage
                  providers must report health insurance coverage information to
                  the IRS each year using Forms{' '}
                  <Link
                    href="/resources/information-returns/form-1095-b-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1095-B
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1095-C
                  </Link>
                  . For businesses managing coverage for dozens — or even
                  thousands — of employees, filing these forms individually can
                  be time-consuming and prone to errors.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With TaxZerone’s ACA Forms Bulk Upload feature, you can{' '}
                  <Link
                    sx={{ color: '#0202ff', cursor: 'pointer' }}
                    onClick={() => {
                      setIsBulkUploadTemplate(true);
                    }}
                  >
                    download template
                  </Link>
                  , enter your employee data, and upload all records at once
                  using a pre-formatted Excel file. In just a few steps, you can
                  validate, process, and{' '}
                  <Link href="/aca-forms/" sx={{ color: '#0202ff' }}>
                    e-file your ACA forms
                  </Link>{' '}
                  directly with the IRS — with the 1094-B or 1094-C transmittal
                  generated automatically.
                </Typography>
                <Box sx={{ pt: 2 }}>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <span style={{ fontWeight: 600 }}>Table of Contents </span>
                  </Typography>

                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file ACA forms?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#supported" sx={{ color: '#0202ff' }}>
                          Supported ACA Forms
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#file" sx={{ color: '#0202ff' }}>
                          File Formats & Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#bulk" sx={{ color: '#0202ff' }}>
                          How to Bulk Upload ACA Forms with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#submission" sx={{ color: '#0202ff' }}>
                          Submission Status guide
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText id="who">
                        <Link href="#deadlines" sx={{ color: '#0202ff' }}>
                          ACA Filing Deadlines for Tax Year 2025
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who must file ACA Forms?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  ACA reporting requirements differ depending on the type and
                  size of your organization:
                </Typography>

                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table aria-label="Employer Type Table">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 250,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Employer Type
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 250,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Form Required
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 250,
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Threshold
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Applicable Large Employers (ALEs)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Forms 1094-C & 1095-C
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            50+ full-time equivalent employees
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Self-insured small employers
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Forms 1094-B & 1095-B
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Fewer than 50 FTEs but offer self-insured coverage
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Insurance providers
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Forms 1094-B & 1095-B
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Any insurer providing minimum essential coverage
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Government-sponsored programs
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Forms 1094-B & 1095-B
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Medicare, Medicaid, CHIP, TRICARE, etc.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  id="supported"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Note:</span> Employers
                  filing 10 or more information returns of any type in a
                  calendar year are required to file electronically with the
                  IRS.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Supported Forms for Bulk Upload
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  TaxZerone's Bulk Upload feature supports all four ACA
                  reporting forms. The transmittal forms are generated
                  automatically — you only need to upload employee-level data.
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table aria-label="Form Details Table">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 200,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Form
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 350,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Full Name
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 250,
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Auto-Generated
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            1095-B
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Health Coverage (individual recipient copy)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Upload required
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            1094-B
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Transmittal of Health Coverage Information Returns
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Auto-generated by TaxZerone
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            1095-C
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Employer-Provided Health Insurance Offer and
                            Coverage
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Upload required
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            id="file"
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            1094-C
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Transmittal of Employer-Provided Health Insurance
                            Offer and Coverage
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Auto-generated by TaxZerone
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  File Format & Template Requirements
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Accepted File Format
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone accepts xslx, xls, csv files for ACA bulk uploads. A
                  pre-formatted template is provided for each form type —
                  download it from your information returns Dashboard, fill it
                  in, and upload it.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Template Rules
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 4,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    Always use the{' '}
                    <span style={{ fontWeight: 600 }}>
                      current tax year template
                    </span>{' '}
                    — codes and fields are updated annually.{' '}
                  </li>
                  <li>
                    Do not add, remove, or rename columns in the template.{' '}
                  </li>

                  <li>
                    Each row represents one{' '}
                    <span style={{ fontWeight: 600 }}>employee</span> or{' '}
                    <span style={{ fontWeight: 600 }}>recipient record</span>
                    .{' '}
                  </li>
                  <li>
                    Date fields must use the format{' '}
                    <span style={{ fontWeight: 600 }}>MM/DD/YYYY</span> (e.g.,
                    03/01/2025).{' '}
                  </li>
                  <li>
                    SSN and EIN must be entered as{' '}
                    <span style={{ fontWeight: 600 }}>
                      9-digit numbers without dashes
                    </span>
                    .
                  </li>
                  <li>
                    Dollar amounts must be{' '}
                    <span style={{ fontWeight: 600 }}>
                      numeric with up to 2 decimal places
                    </span>{' '}
                    — no currency symbols or commas.
                  </li>
                  <li>
                    Leave optional fields{' '}
                    <span style={{ fontWeight: 600 }}>blank</span> if not
                    applicable — do not enter "N/A" or "0".
                  </li>
                  <li>
                    Remove all blank rows, merged cells, or extra formatting
                    before uploading.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Key Fields Required for Form 1095-C
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table aria-label="Field Requirements Table">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Field
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Required
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Employee SSN
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Yes
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Employee Name (First, Last)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Yes
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Employer EIN
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Yes
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Coverage Offer Code (Line 14)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Yes
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Employee Share Amount (Line 15)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Yes
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Safe Harbor Code (Line 16)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Conditional
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Plan Start Month
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Yes
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Tax Year
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Yes
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  id="bulk"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '75%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        ACA Bulk Upload — File Multiple Forms in Minutes
                      </Typography>
                      <Typography variant="body2">
                        With TaxZerone’s{' '}
                        <span style={{ fontWeight: 600 }}>Bulk Upload</span>,
                        you can submit multiple Forms 1095-B or 1095-C at once,
                        validate your data for errors, and e-file directly with
                        the IRS — quick, accurate, and easy.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 180, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        onClick={() => {
                          setIsBulkUploadTemplate(true);
                        }}
                        size="large"
                      >
                        Download the Free Template
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to Bulk Upload ACA Forms with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Follow these steps to prepare, upload, and{' '}
                  <Link href="/aca-forms/" sx={{ color: '#0220ff' }}>
                    e-file ACA forms
                  </Link>{' '}
                  in one submission:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1.5,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Sign In or Create an Account
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 3,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    If you're new to TaxZerone, click{' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/`}
                      sx={{ color: '#0202ff' }}
                    >
                      Sign Up
                    </Link>{' '}
                    to create your account in just a few minutes.
                  </li>
                  <li>
                    Already registered? Simply click{' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signin/`}
                      sx={{ color: '#0202ff' }}
                    >
                      Sign In
                    </Link>{' '}
                    to log in and go to your dashboard.
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,

                    fontWeight: 600,
                  }}
                >
                  Step 2: Go to the TaxZerone Dashboard
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  After logging in:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    Click <span style={{ fontWeight: 600 }}>Start</span> in the
                    upper-right corner and navigate to{' '}
                    <span style={{ fontWeight: 600 }}>Download</span>.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/aca-form-bulk-upload-start-download-img.webp" />
                  <source srcSet="/assets/images/aca-form-bulk-upload-start-download-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-form-bulk-upload-start-download-img.png"
                    loading="lazy"
                    alt=" ACA form bulk upload-download"
                    width={720}
                    height={468}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    Select{' '}
                    <span style={{ fontWeight: 600 }}>Information Returns</span>{' '}
                    under the{' '}
                    <span style={{ fontWeight: 600 }}>
                      Download Bulk Upload Template
                    </span>{' '}
                    section.
                  </li>
                  <li>
                    Choose the <span style={{ fontWeight: 600 }}>Tax Year</span>
                    , then select the ACA forms you want to download{' '}
                    <span style={{ fontWeight: 600 }}>
                      (Form 1095-B or Form 1095-C)
                    </span>
                    .
                  </li>
                </Box>

                <picture>
                  <source srcSet="/assets/images/aca-form-bulk-upload-download-bulk-upload-template-img.webp" />
                  <source srcSet="/assets/images/aca-form-bulk-upload-download-bulk-upload-template-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-form-bulk-upload-download-bulk-upload-template-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={720}
                    height={531}
                    className="img-responsive"
                    sx={{
                      // border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Start a New Form from the Dashboard
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    Click <span style={{ fontWeight: 600 }}>Start</span> and
                    select <span style={{ fontWeight: 600 }}>New Form</span>.
                  </li>
                </Box>

                <picture>
                  <source srcSet="/assets/images/aca-form-bulk-upload-new-form-img.webp" />
                  <source srcSet="/assets/images/aca-form-bulk-upload-new-form-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-form-bulk-upload-new-form-img.png"
                    loading="lazy"
                    alt="ACA form bulk upload-start new form"
                    width={720}
                    height={466}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    Under <span style={{ fontWeight: 600 }}>Start Filing</span>,
                    go to{' '}
                    <span style={{ fontWeight: 600 }}>Information Returns</span>{' '}
                    and Choose{' '}
                    <span style={{ fontWeight: 600 }}>Bulk Upload</span>, then
                    select <span style={{ fontWeight: 600 }}>Form 1095-B</span>{' '}
                    or <span style={{ fontWeight: 600 }}>Form 1095-C</span>.
                  </li>
                </Box>

                <picture>
                  <source srcSet="/assets/images/aca-form-bulk-upload-start-filing-bulk-upload-img.webp" />
                  <source srcSet="/assets/images/aca-form-bulk-upload-start-filing-bulk-upload-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-form-bulk-upload-start-filing-bulk-upload-img.png"
                    loading="lazy"
                    alt="aca-form-bulk-upload-start-filing-bulk-upload"
                    width={720}
                    height={407}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    If you are a new user, click{' '}
                    <span style={{ fontWeight: 600 }}>Add New Payer</span> or if
                    you already have a saved payer, select it from the{' '}
                    <span style={{ fontWeight: 600 }}>Address Book</span>.
                  </li>
                </Box>

                <picture>
                  <source srcSet="/assets/images/aca-form-bulk-upload-start-add-new-or-select-address-book-img.webp" />
                  <source srcSet="/assets/images/aca-form-bulk-upload-start-add-new-or-select-address-book-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-form-bulk-upload-start-add-new-or-select-address-book-img.png"
                    loading="lazy"
                    alt="aca-form-bulk-upload-start-add-new-or-select-address-book"
                    width={720}
                    height={277}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    Select the <span style={{ fontWeight: 600 }}>Tax Year</span>{' '}
                    and Choose the{' '}
                    <span style={{ fontWeight: 600 }}>business</span> for which
                    you want to file{' '}
                    <span style={{ fontWeight: 600 }}>
                      Form 1095-B or Form 1095-C
                    </span>
                    .
                  </li>
                </Box>

                <picture>
                  <source srcSet="/assets/images/aca-form-bulk-upload-start-new-form-1095-b-img.webp" />
                  <source srcSet="/assets/images/aca-form-bulk-upload-start-new-form-1095-b-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-form-bulk-upload-start-new-form-1095-b-img.png"
                    loading="lazy"
                    alt="ACA form bulk upload- start a new form 1095-b-select the tax year and business"
                    width={720}
                    height={230}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Step 4: Upload Your Completed File
                </Typography>
                <picture>
                  <source srcSet="/assets/images/aca-form-bulk-upload-browse-the-file-select-img.webp" />
                  <source srcSet="/assets/images/aca-form-bulk-upload-browse-the-file-select-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-form-bulk-upload-browse-the-file-select-img.png"
                    loading="lazy"
                    alt=" ACA Forms bulk upload- upload a file"
                    width={720}
                    height={460}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    Click <span style={{ fontWeight: 600 }}>Browse</span>,
                    locate the file you want to upload from your device, and
                    select it. Then click Upload to{' '}
                    <span style={{ fontWeight: 600 }}>upload</span> the file
                    successfully.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Note:</span> Always use the
                  template for the{' '}
                  <span style={{ fontWeight: 600 }}>current tax year</span>. ACA
                  code tables and field requirements are updated annually to
                  reflect IRS changes. Using a prior-year template may result in
                  validation errors or IRS rejection.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Step 6: Process Your File
                </Typography>
                <picture>
                  <source srcSet="/assets/images/aca-form-bulk-upload-process-file-img.webp" />
                  <source srcSet="/assets/images/aca-form-bulk-upload-process-file-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-form-bulk-upload-process-file-img.png"
                    loading="lazy"
                    alt="ACA Forms bulk upload-process file"
                    width={720}
                    height={351}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    After reviewing all the records, click{' '}
                    <span style={{ fontWeight: 600 }}>Process File</span> to
                    prepare your submission.
                  </li>
                  <li>
                    Review the filing summary — confirm the{' '}
                    <span style={{ fontWeight: 600 }}>record count</span>, form
                    type, and tax year are correct before proceeding.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Step 7: Review and Correct Errors
                </Typography>
                <picture>
                  <source srcSet="/assets/images/aca-form-bulk-upload-review-&-correct-errors-img.webp" />
                  <source srcSet="/assets/images/aca-form-bulk-upload-review-&-correct-errors-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-form-bulk-upload-review-&-correct-errors-img.png"
                    loading="lazy"
                    alt="ACA Forms bulk upload-review and correct errors"
                    width={720}
                    height={328}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    If errors are found, they will appear in the{' '}
                    <span style={{ fontWeight: 600 }}>Error Records</span> tab.
                    Correct them in your Excel file and re-upload it. If no
                    errors are found, you can proceed to the next step.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Note:</span> There is no
                  additional charge for re-uploading a corrected file. Keep
                  correcting and re-uploading until your file passes validation
                  with zero critical errors.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Step 8: Pay & E-File
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    Enter your payment info and click{' '}
                    <span style={{ fontWeight: 600 }}>"Pay & e-File"</span> to
                    securely submit your forms. TaxZerone will keep you updated,
                    and you’ll receive confirmation once accepted.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Step 9: Deliver Recipient Copies
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    textAlign: 'left',
                    mt: 1,
                    mb: 2,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    After e-filing, you are required to furnish copies of Form
                    1095-B or 1095-C to your employees/recipients.
                  </li>
                  <li>
                    Review the filing summary and enter the recipient’s{' '}
                    <span style={{ fontWeight: 600 }}>
                      email address for electronic delivery through ZeroneVault
                    </span>{' '}
                    or provide the{' '}
                    <span style={{ fontWeight: 600 }}>
                      postal mailing address
                    </span>{' '}
                    if you choose to send the copies by mail.
                  </li>
                </Box>
                <Alert
                  id="submission"
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Note:</span> The recipient
                  copy deadline for Tax Year 2025 is{' '}
                  <span style={{ fontWeight: 600 }}>March 2, 2026</span>.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Understanding Submission Status
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can track your ACA filing progress directly in{' '}
                  <Link
                    href="https://www.taxzerone.com/"
                    sx={{ color: '#0220ff' }}
                  >
                    TaxZerone
                  </Link>{' '}
                  through the{' '}
                  <span style={{ fontWeight: 600 }}>
                    Information Returns Dashboard
                  </span>
                  . Here’s what each submission status means and the action
                  required:
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table aria-label="Status Table">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 200,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Status
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            What It Means
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Action Required
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            In-progress
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Your return is being prepared or validated and has
                            not yet been transmitted to the IRS.
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Review the form if needed and complete the filing
                            process.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Transmitted
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Your return has been successfully sent to the IRS.
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Wait for the IRS to process the submission and
                            update the status.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Accepted
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            The IRS has accepted your filing.
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            No further IRS action is required. Proceed with
                            distributing recipient copies if applicable.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            id="deadlines"
                            sx={{
                              borderRight: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Rejected
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            The IRS has rejected your submission due to errors
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            Review the rejection details, correct the errors,
                            and resubmit the return through the app.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1.5,
                  }}
                >
                  ACA Filing Deadlines for Tax Year 2025
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table aria-label="Requirements Deadlines Table">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 400,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Requirements
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Furnish recipient copies (1095-B / 1095-C) to
                            employees
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            March 2, 2026
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Paper filing with IRS
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            March 2, 2026
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Electronic filing with IRS (10 or more forms)
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            March 31, 2026
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0, ml: 4 }}>
                    <Link
                      href="/resources/information-returns/aca-forms-penalties/"
                      sx={{ color: '#0220ff' }}
                    >
                      Failure to file ACA forms
                    </Link>{' '}
                    on time may result in IRS penalties starting at{' '}
                    <span style={{ fontWeight: 600 }}>$340 per return</span>, up
                    to a maximum of $4,098,500 per year for large employers.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 1.5,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-File Your ACA Forms Today with TaxZerone!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Bulk upload Forms 1095-B and 1095-C quickly and accurately —
              starting at just <b>$0.59 per form.</b> <br />
              IRS-authorized. No tax knowledge is required.
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=aca-forms-bulk-upload`}
              >
                E-file now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default ACAFormsBulkUpload;
